import { useCallback, useContext, useEffect, useState, useRef } from "react";
import {
    ToastActionType,
    useDelayedActionToast,
    TableContext,
    useApiContextFetch,
} from "@swiggy-private/portal-dineout-context";
import { useActions } from "./useActions";
import { useIsV2InApp } from "./useIsV2InApp";
import { triggerQuickAction } from "../api/quickActions";
import { SUCCESS_STATUS_CODE } from "../constants/api";
import { IQuickAction } from "../utils/types";

export const useQuickActions = (options?: { skipPinCheck?: boolean }) => {
    const { skipPinCheck = false } = options || {};
    const { handleLogin, handlePin } = useActions();
    const { toastState, dispatch: dispatchDelayedActionToast } = useDelayedActionToast();
    const makeApiCall = useApiContextFetch();
    const {
        tableId = "",
        restaurantId = "",
        parentOrderId = "",
        verifyPinFlow,
        setVerifyPinFlow,
    } = useContext(TableContext);
    const [isToastVisible, setIsToastVisible] = useState(false);
    const isMountedRef = useRef(true);

    const { isV2InApp } = useIsV2InApp();

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (isMountedRef.current) {
            dispatchDelayedActionToast({
                type: ToastActionType.HIDE_TOAST,
            });
        }
    }, [dispatchDelayedActionToast]);

    useEffect(() => {
        if (isMountedRef.current) {
            setIsToastVisible(toastState?.isVisible || false);
        }
    }, [toastState]);

    const onQuickActionButtonClick = useCallback(
        (quickAction: IQuickAction) => {
            if (isToastVisible) {
                return;
            }

            if (!handleLogin() || !makeApiCall) {
                return;
            }

            // Skip pin check for V2 in-app OR if skipPinCheck is true.
            if (!isV2InApp && !skipPinCheck && !handlePin()) {
                return;
            }

            const actionForRequestApi = async () => {
                if (!quickAction?.actionButton?.id || !quickAction?.actionButton?.name) {
                    throw new Error("Action failed");
                }

                const requestParams = {
                    tableId,
                    restaurantId,
                    actionId: quickAction?.actionButton?.id?.toString() || "",
                    actionName: quickAction?.actionButton?.name || "",
                    ...(parentOrderId && { parentOrderId }),
                };

                const response = await triggerQuickAction(requestParams, makeApiCall);
                if (!isMountedRef.current) {
                    return;
                }

                if (response?.statusCode !== SUCCESS_STATUS_CODE) {
                    // Throwing direct error as the Toast is showing Retry button on error
                    throw new Error("Action failed");
                }
            };

            if (isMountedRef.current) {
                dispatchDelayedActionToast({
                    type: ToastActionType.SHOW_TOAST,
                    payload: {
                        props: {
                            actionIcon: quickAction?.actionButton?.icon?.id,
                            delayActionDuration: quickAction?.seconds,
                            successIcon: quickAction?.successIcon,
                            onAction: actionForRequestApi,
                            cancelButtonStyle: quickAction?.cancelButtonStyle,
                            retryButtonStyle: quickAction?.retryButtonStyle,
                        },
                        style: quickAction?.style,
                    },
                });
            }
        },
        [
            handleLogin,
            handlePin,
            makeApiCall,
            dispatchDelayedActionToast,
            tableId,
            restaurantId,
            parentOrderId,
            isToastVisible,
            isV2InApp,
            skipPinCheck,
        ],
    );

    return {
        onQuickActionButtonClick,
        verifyPinFlow,
        setVerifyPinFlow,
    };
};
